import { useState, useEffect } from 'react';
import { ArrowLeft, Save, X } from 'lucide-react';
import { databaseService } from '../../../lib/appwrite';

const PAGE_TYPES = [
  { value: 'projects', label: 'Projects Page', basePath: '/projects' },
  { value: 'spatial-canvas', label: 'Spatial Canvas Page', basePath: '/spatial-canvas' },
  { value: 'certificates', label: 'Certificates Page', basePath: '/certificates' },
  { value: 'blogs', label: 'Blogs Page', basePath: '/blogs' }
];

export default function WorkLinkForm({ link, availableLinks, onSubmit, onCancel }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    pageType: '',
    category: '',
    subcategory: '',
    tags: [],
    generatedUrl: ''
  });
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableSubcategories, setAvailableSubcategories] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (link) {
      setFormData({
        name: link.name || '',
        description: link.description || '',
        pageType: link.pageType || '',
        category: link.category || '',
        subcategory: link.subcategory || '',
        tags: link.tags || [],
        generatedUrl: link.generatedUrl || ''
      });
    }
  }, [link]);

  // Load categories when page type changes
  useEffect(() => {
    if (formData.pageType) {
      loadCategoriesForPage(formData.pageType);
      
      // Only reset if not editing an existing link
      if (!link) {
        setFormData(prev => ({
          ...prev,
          category: '',
          subcategory: '',
          tags: []
        }));
      }
    }
  }, [formData.pageType]);

  // Load subcategories when category changes (for projects page)
  useEffect(() => {
    if (formData.pageType === 'projects' && formData.category) {
      console.log('Loading subcategories for category:', formData.category);
      loadSubcategoriesForCategory(formData.category);
    } else {
      setAvailableSubcategories([]);
      // Only reset subcategory if category is cleared
      if (!formData.category) {
        setFormData(prev => ({ ...prev, subcategory: '' }));
      }
    }
  }, [formData.pageType, formData.category]);

  // Load tags when page type, category, or subcategory changes
  useEffect(() => {
    if (formData.pageType) {
      loadTagsForPage(formData.pageType, formData.category, formData.subcategory);
    }
  }, [formData.pageType, formData.category, formData.subcategory]);

  const loadCategoriesForPage = async (pageType) => {
    try {
      let collectionName = '';
      
      switch (pageType) {
        case 'projects':
          collectionName = 'categories';
          break;
        case 'spatial-canvas':
          collectionName = 'map_categories';
          break;
        case 'certificates':
          collectionName = 'certificate_categories';
          break;
        case 'blogs':
          collectionName = 'blog_categories';
          break;
        default:
          return;
      }

      const result = await databaseService.listDocuments(collectionName);
      if (result.success) {
        const categories = result.data.documents
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map(cat => cat.name);
        setAvailableCategories(categories);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadSubcategoriesForCategory = async (categoryName) => {
    try {
      console.log('Fetching subcategories for category:', categoryName);
      
      // First, get the category ID from the category name
      const categoriesResult = await databaseService.listDocuments('categories');
      if (!categoriesResult.success) {
        console.error('Failed to load categories');
        setAvailableSubcategories([]);
        return;
      }
      
      const category = categoriesResult.data.documents.find(cat => cat.name === categoryName);
      if (!category) {
        console.log('Category not found:', categoryName);
        setAvailableSubcategories([]);
        return;
      }
      
      console.log('Found category:', category);
      const categoryId = category.$id;
      
      // Now fetch subcategories using categoryId
      const result = await databaseService.listDocuments('subcategories');
      console.log('Subcategories result:', result);
      
      if (result.success) {
        const allSubcategories = result.data.documents;
        console.log('All subcategories:', allSubcategories);
        
        // Filter by categoryId instead of categoryName
        const filtered = allSubcategories.filter(sub => sub.categoryId === categoryId);
        console.log('Filtered subcategories:', filtered);
        
        const subcategories = filtered
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map(sub => sub.name);
        
        console.log('Final subcategories list:', subcategories);
        setAvailableSubcategories(subcategories);
      }
    } catch (error) {
      console.error('Error loading subcategories:', error);
      setAvailableSubcategories([]);
    }
  };

  const loadTagsForPage = async (pageType, category, subcategory) => {
    try {
      let collectionName = '';
      
      switch (pageType) {
        case 'projects':
          collectionName = 'projects';
          break;
        case 'spatial-canvas':
          collectionName = 'spatial_maps';
          break;
        case 'certificates':
          collectionName = 'certificates';
          break;
        case 'blogs':
          collectionName = 'blogs';
          break;
        default:
          return;
      }

      const result = await databaseService.listDocuments(collectionName);
      if (result.success) {
        let items = result.data.documents;
        
        // Filter by category if selected
        if (category) {
          items = items.filter(item => item.category === category);
        }
        
        // Filter by subcategory if selected (for projects)
        // Note: Projects store subcategoryId (ID), not subcategory (name)
        if (subcategory && pageType === 'projects') {
          // Get the subcategory ID from the name
          const subcategoriesResult = await databaseService.listDocuments('subcategories');
          if (subcategoriesResult.success) {
            const subcategoryDoc = subcategoriesResult.data.documents.find(sub => sub.name === subcategory);
            if (subcategoryDoc) {
              const subcategoryId = subcategoryDoc.$id;
              items = items.filter(item => item.subcategoryId === subcategoryId);
            }
          }
        }
        
        // Extract all unique tags
        const allTags = items.flatMap(item => item.tags || []);
        const uniqueTags = [...new Set(allTags)].filter(Boolean).sort();
        setAvailableTags(uniqueTags);
      }
    } catch (error) {
      console.error('Error loading tags:', error);
    }
  };

  // Generate URL whenever filters change
  useEffect(() => {
    if (formData.pageType) {
      const pageConfig = PAGE_TYPES.find(p => p.value === formData.pageType);
      if (!pageConfig) return;

      let url = pageConfig.basePath;
      const params = new URLSearchParams();

      if (formData.category) {
        params.append('category', formData.category);
      }

      if (formData.subcategory) {
        params.append('subcategory', formData.subcategory);
      }

      if (formData.tags.length > 0) {
        params.append('tags', formData.tags.join(','));
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      setFormData(prev => ({ ...prev, generatedUrl: url }));
    }
  }, [formData.pageType, formData.category, formData.subcategory, formData.tags]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTagToggle = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.pageType) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      await onSubmit(formData);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={onCancel}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" style={{ color: '#4C1D95' }} />
        </button>
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#4C1D95' }}>
            {link ? 'Edit Work Link' : 'Create Work Link'}
          </h1>
          <p className="text-gray-600 mt-1">
            Configure filtered URL for your content
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="max-w-3xl">
        <div className="bg-white rounded-lg shadow-lg p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold mb-4" style={{ color: '#4C1D95' }}>
              Basic Information
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Link Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., GIS Projects, Remote Sensing Blogs"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2"
                  style={{ borderColor: '#4C1D95' }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="2"
                  placeholder="Brief description of what this link shows"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2"
                  style={{ borderColor: '#4C1D95' }}
                />
              </div>
            </div>
          </div>

          {/* Page Selection */}
          <div>
            <h3 className="text-lg font-semibold mb-4" style={{ color: '#4C1D95' }}>
              Page Selection
            </h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Page Type *
              </label>
              <select
                name="pageType"
                value={formData.pageType}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2"
                style={{ borderColor: '#4C1D95' }}
              >
                <option value="">Choose a page...</option>
                {PAGE_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Filters */}
          {formData.pageType && (
            <div>
              <h3 className="text-lg font-semibold mb-4" style={{ color: '#4C1D95' }}>
                Filters (Optional)
              </h3>
              
              <div className="space-y-4">
                {/* Category Filter */}
                {availableCategories.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Filter by Category
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2"
                      style={{ borderColor: '#4C1D95' }}
                    >
                      <option value="">All Categories</option>
                      {availableCategories.map(cat => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Optional: Select a category to filter content
                    </p>
                  </div>
                )}

                {/* Subcategory Filter (only for Projects page) */}
                {formData.pageType === 'projects' && formData.category && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Filter by Subcategory
                    </label>
                    {availableSubcategories.length > 0 ? (
                      <>
                        <select
                          name="subcategory"
                          value={formData.subcategory}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2"
                          style={{ borderColor: '#4C1D95' }}
                        >
                          <option value="">All Subcategories</option>
                          {availableSubcategories.map(sub => (
                            <option key={sub} value={sub}>
                              {sub}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Optional: Select a subcategory to further filter content
                        </p>
                      </>
                    ) : (
                      <div className="w-full px-4 py-3 border rounded-lg bg-gray-50 text-gray-500 text-sm">
                        No subcategories found for "{formData.category}" category.
                        <br />
                        <span className="text-xs">Add subcategories in Category Management first.</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Tags Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Filter by Tags
                  </label>
                  {availableTags.length > 0 ? (
                    <>
                      <div className="border rounded-lg p-4" style={{ borderColor: '#4C1D95' }}>
                        <div className="flex flex-wrap gap-2">
                          {availableTags.map(tag => (
                            <button
                              key={tag}
                              type="button"
                              onClick={() => handleTagToggle(tag)}
                              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-300 ${
                                formData.tags.includes(tag)
                                  ? 'bg-[#4C1D95] text-white'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              {tag}
                            </button>
                          ))}
                        </div>
                        {formData.tags.length > 0 && (
                          <div className="mt-3 pt-3 border-t">
                            <p className="text-sm text-gray-600">
                              Selected: {formData.tags.join(', ')}
                            </p>
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Optional: Select one or multiple tags to filter content
                      </p>
                    </>
                  ) : (
                    <div className="w-full px-4 py-3 border rounded-lg bg-gray-50 text-gray-500 text-sm">
                      {formData.category || formData.subcategory ? (
                        <>
                          No tags found for the selected filters.
                          <br />
                          <span className="text-xs">You can still create the link without tags, or add tags to your projects first.</span>
                        </>
                      ) : (
                        <>
                          Select a category or subcategory to see available tags.
                          <br />
                          <span className="text-xs">Tags will be filtered based on your selection.</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Generated URL Preview */}
          {formData.generatedUrl && (
            <div>
              <h3 className="text-lg font-semibold mb-4" style={{ color: '#4C1D95' }}>
                Generated URL
              </h3>
              <div className="p-4 bg-gray-50 rounded-lg border">
                <p className="text-sm text-gray-600 mb-2">This URL will be created:</p>
                <code className="block p-3 bg-white rounded border text-sm font-mono text-gray-800 break-all">
                  {window.location.origin}{formData.generatedUrl}
                </code>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="flex items-center gap-2 px-6 py-2 border rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.name || !formData.pageType}
              className="flex items-center gap-2 px-6 py-2 rounded-lg text-white transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#4C1D95' }}
            >
              <Save className="w-4 h-4" />
              {loading ? 'Saving...' : link ? 'Update Link' : 'Create Link'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

